import { useState, useRef, ChangeEvent, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';

const MAX_IMAGES = 4;

interface PreviewImage {
  id: string;
  base64: string;
  name: string;
}

const compressImage = (file: File, maxWidth = 1000): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });

const VenderForm = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [images, setImages] = useState<PreviewImage[]>([]);
  const [charCount, setCharCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageAdd = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = MAX_IMAGES - images.length;
    const toProcess = files.slice(0, remaining);
    const compressed = await Promise.all(
      toProcess.map(async (f) => ({
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        base64: await compressImage(f),
        name: f.name,
      }))
    );
    setImages((prev) => [...prev, ...compressed]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (id: string) => setImages((prev) => prev.filter((i) => i.id !== id));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    const form = e.currentTarget;
    const fd = new FormData(form);

    const nombre = fd.get('nombre') as string;
    const telefono = fd.get('telefono') as string;
    const email = fd.get('email') as string;
    const marca = fd.get('marca') as string;
    const modelo = fd.get('modelo') as string;
    const anio = fd.get('anio') as string;
    const matricula = fd.get('matricula') as string;
    const kilometros = fd.get('kilometros') as string;
    const combustible = fd.get('combustible') as string;
    const estado = fd.get('estado') as string;
    const precio_esperado = fd.get('precio_esperado') as string;
    const descripcion = fd.get('descripcion') as string;

    const mensaje = [
      `--- SOLICITUD DE VENTA DE VEHÍCULO ---`,
      `Vehículo: ${marca} ${modelo} (${anio})`,
      `Matrícula: ${matricula}`,
      `Kilómetros: ${kilometros} km`,
      `Combustible: ${combustible}`,
      `Estado: ${estado}`,
      precio_esperado ? `Precio esperado: ${precio_esperado} €` : '',
      `Fotos adjuntas: ${images.length > 0 ? `${images.length} imagen(es)` : 'Sin fotos'}`,
      descripcion ? `\nDescripción adicional:\n${descripcion}` : '',
    ].filter(Boolean).join('\n');

    const imagenesBase64 = images.map((img) => img.base64);

    try {
      const { error } = await supabase
        .from('TALLERMECANICOKEVIN_contact_messages')
        .insert({ nombre, telefono, email, servicio: 'Venta de vehículo', mensaje, imagenes: imagenesBase64 });

      if (error) {
        setErrorMsg(error.message);
        setStatus('error');
      } else {
        setStatus('ok');
        form.reset();
        setImages([]);
        setCharCount(0);
        setShowPreview(false);
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error desconocido');
      setStatus('error');
    }
  };

  if (status === 'ok') {
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-4">
        <div className="w-16 h-16 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 text-3xl">
          <i className="ri-car-line" />
        </div>
        <p className="text-white font-black text-lg uppercase tracking-wide">¡Solicitud enviada!</p>
        <p className="text-zinc-400 text-sm text-center max-w-xs">Hemos recibido los datos de tu vehículo. Nos pondremos en contacto contigo en menos de 24 horas con una valoración.</p>
        <button onClick={() => setStatus('idle')} className="mt-2 text-xs text-yellow-400 hover:text-white transition-colors cursor-pointer uppercase tracking-widest">
          Enviar otro vehículo
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 text-black">
          <i className="ri-car-line text-sm" />
        </div>
        <h2 className="text-white font-black text-lg uppercase">Vende tu coche</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-6">Rellena el formulario y nuestro equipo valorará tu vehículo sin compromiso.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="border-l-2 border-yellow-400/40 pl-3 mb-1"><p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest">Tus datos</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Nombre *</label>
            <input name="nombre" required placeholder="Tu nombre completo" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Teléfono *</label>
            <input name="telefono" required type="tel" placeholder="6XX XXX XXX" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Email *</label>
          <input name="email" required type="email" placeholder="tu@email.com" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
        </div>

        <div className="border-l-2 border-yellow-400/40 pl-3 mt-5 mb-1"><p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest">Datos del vehículo</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Marca *</label>
            <input name="marca" required placeholder="Ej: BMW, Seat..." className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Modelo *</label>
            <input name="modelo" required placeholder="Ej: Serie 3, León..." className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Año *</label>
            <input name="anio" required type="number" min="1980" max={new Date().getFullYear()} placeholder="2018" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Matrícula *</label>
            <input name="matricula" required placeholder="0000 XXX" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600 uppercase" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Km *</label>
            <input name="kilometros" required type="number" min="0" placeholder="120000" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Combustible *</label>
            <select name="combustible" required className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="">Seleccionar</option>
              <option>Gasolina</option><option>Diésel</option><option>Híbrido</option><option>Eléctrico</option><option>GLP / Gas</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Estado</label>
            <select name="estado" className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="">Seleccionar</option>
              <option>Excelente — Como nuevo</option><option>Bueno — Pequeños detalles</option><option>Regular — Necesita revisión</option><option>Para reparar</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Precio esperado (€)</label>
          <input name="precio_esperado" type="number" min="0" placeholder="Opcional" className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
            Descripción <span className="text-zinc-600 normal-case font-normal">{charCount}/500</span>
          </label>
          <textarea name="descripcion" maxLength={500} rows={3} onChange={(e) => setCharCount(e.target.value.length)}
            placeholder="Extras, historial, ITV, daños..."
            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors resize-none placeholder:text-zinc-600" />
        </div>

        {/* Image upload */}
        <div className="border-l-2 border-yellow-400/40 pl-3 mb-1">
          <p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest">Fotos del vehículo</p>
          <p className="text-zinc-600 text-xs mt-0.5">Máximo 4 fotos. Ayudan a valorar mejor tu coche.</p>
        </div>
        <div className="space-y-3">
          {images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {images.map((img) => (
                <div key={img.id} className="relative group aspect-square bg-zinc-800 border border-zinc-700 overflow-hidden">
                  <img src={img.base64} alt={img.name} className="w-full h-full object-cover object-top" />
                  <button type="button" onClick={() => handleRemoveImage(img.id)}
                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/80 text-white hover:bg-red-600 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                    <i className="ri-close-line text-xs" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-1">
                    <p className="text-white text-[10px] truncate">{img.name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {images.length < MAX_IMAGES && (
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-zinc-700 hover:border-yellow-400/40 text-zinc-500 hover:text-yellow-400/80 transition-all py-4 text-xs font-bold uppercase tracking-widest cursor-pointer">
              <i className="ri-image-add-line text-base" />
              {images.length === 0 ? 'Añadir fotos (máx. 4)' : `Añadir más (${images.length}/${MAX_IMAGES})`}
            </button>
          )}
          {images.length >= MAX_IMAGES && (
            <p className="text-yellow-400/70 text-xs text-center font-bold uppercase tracking-widest py-1">
              <i className="ri-image-line mr-1" /> Máximo alcanzado ({MAX_IMAGES}/{MAX_IMAGES})
            </p>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageAdd} className="hidden" />
        </div>

        {/* Preview toggle */}
        {images.length > 0 && (
          <button type="button" onClick={() => setShowPreview(!showPreview)}
            className="w-full flex items-center justify-center gap-2 border border-zinc-700 py-2.5 text-zinc-400 hover:text-white text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors">
            <i className={`${showPreview ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
            {showPreview ? 'Ocultar' : 'Ver'} vista previa del envío
          </button>
        )}

        {showPreview && images.length > 0 && (
          <div className="bg-zinc-800/50 border border-zinc-700 p-4 space-y-3">
            <p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
              <i className="ri-file-list-3-line" /> Vista previa de fotos
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {images.map((img) => (
                <div key={img.id} className="aspect-square bg-zinc-800 overflow-hidden border border-zinc-700">
                  <img src={img.base64} alt={img.name} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
          </div>
        )}

        {status === 'error' && (
          <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-3 py-2">
            <i className="ri-error-warning-line mr-1" />
            {errorMsg || 'Error al enviar. Inténtalo de nuevo.'}
          </p>
        )}
        <button type="submit" disabled={status === 'sending'}
          className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-black text-xs uppercase tracking-widest py-3.5 hover:bg-yellow-300 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2">
          {status === 'sending' ? <><i className="ri-loader-4-line animate-spin" /> Enviando...</> : <><i className="ri-car-line" /> Enviar solicitud de venta</>}
        </button>
      </form>
    </>
  );
};

export default VenderForm;
